<template>
  <div class="relative w-full h-full">
    <!-- Manual Survey Mode View -->
    <div
      v-if="isManualSurveyMode && apiToken && currentTargetAreaId"
      style="height: calc(100vh - 220px);"
    >
      <ManualSurvey
        :target-area-id="currentTargetAreaId"
        :country-code="props.target_area_data[0].country_iso3_code"
        :center-lat="tcLat"
        :center-lng="tcLng"
        :external-user-id="props.target_area_data[0].current_user_external_id"
        :target-area-name="props.target_area_data[0].name"
        :api-base-url="sparrowBaseUrl"
        :api-key="apiToken"
        :on-token-refresh="handleTokenRefresh"
        :on-auth-failure="handleAuthFailure"
        @surveys-submitted="isManualSurveyMode = false"
        @exit-manual-mode="isManualSurveyMode = false"
      />
    </div>
    <!-- END Manual Survey Mode View -->

    <!-- Main Map View -->
    <div v-if="!isManualSurveyMode" class="container mx-auto z-0 overflow-hidden flex flex-col h-auto md:h-[calc(100vh-220px)]">
      <!-- Pending Surveys Banner -->
      <div v-if="currentTargetAreaId" class="mb-3">
        <PendingSurveysBanner
          :user-id="props.target_area_data[0].current_user_external_id"
          :target-area-id="currentTargetAreaId"
          @continue-surveying="isManualSurveyMode = true"
          @discard="() => {}"
        />
      </div>

      <!-- Header for both normal-view and modal-view modes -->
      <div class="px-4 flex flex-col md:flex-row md:justify-between md:items-center" :class="props.viewOnly ? 'py-2 md:py-4 border-b border-gray-200 bg-gray-50' : 'pb-4 md:pb-4'">
        <!-- Title section - only show in modal-view mode -->
        <div v-if="props.viewOnly" class="flex flex-col md:flex-row md:items-center md:space-x-3">
          <h2 class="text-lg md:text-xl font-semibold text-gray-900 leading-tight">{{ t('modals.view_only_target_area.target_area') }}</h2>
          <span class="text-xs md:text-sm text-gray-500 mt-1 md:mt-0">{{ props.target_area_data[0]?.name || 'Training Center' }}</span>
        </div>
        <div v-else class="flex flex-col md:flex-row md:items-center md:space-x-3">
        </div>

        <!-- Buttons section -->
        <div class="flex items-center space-x-2 mt-2 md:mt-0">
          <!-- Show/Edit mode toggle (only when a TA exists and not in viewOnly or manual survey mode) -->
          <button
            v-if="isEditing && !isEditMode && !props.viewOnly && !isManualSurveyMode"
            @click="isEditMode = true"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors duration-200 shadow-sm"
            :title="t('modals.kestrel_map.edit_target_area')"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            <span class="hidden md:inline">{{ t('modals.kestrel_map.edit_target_area') }}</span>
          </button>
          <button
            v-if="isEditing && isEditMode && !isManualSurveyMode"
            @click="isEditMode = false"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md transition-colors duration-200 shadow-sm"
            :title="t('modals.kestrel_map.view_target_area')"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
            </svg>
            <span class="hidden md:inline">{{ t('modals.kestrel_map.view_target_area') }}</span>
          </button>
          <button
            v-if="!isManualSurveyMode && isEditing && !isEditMode"
            @click="isManualSurveyMode = true"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 shadow-sm"
            title="Manually Enter Surveys"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
            </svg>
            <span class="hidden md:inline">{{ t('modals.kestrel_map.manual_survey') }}</span>
          </button>
          <!-- Secure PDF checkbox -->
          <label
            v-if="!isManualSurveyMode && isEditing && !isEditMode"
            class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 cursor-pointer"
            :title="t('modals.kestrel_map.secure_pdf_tooltip')"
          >
            <input
              type="checkbox"
              v-model="generateSecurePdf"
              class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <svg class="ml-1.5 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
            <span class="ml-1 hidden md:inline">{{ t('modals.kestrel_map.secure_pdf') }}</span>
          </label>
          <!-- PDF Generation button -->
          <button
            v-if="!isManualSurveyMode && isEditing && !isEditMode"
            @click="generatePDF"
            :disabled="isPdfGenerating"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors duration-200 shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            title="Generate PDF"
          >
            <svg v-if="!isPdfGenerating" class="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="white" stroke="none">
              <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"/>
            </svg>
            <svg v-else class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="hidden md:inline">{{ isPdfGenerating ? t('modals.kestrel_map.generating_pdf') : t('modals.kestrel_map.generate_pdf') }}</span>
          </button>
          <!-- Edit button - only show in modal-view mode -->
          <button
            v-if="props.viewOnly"
            @click="editTargetArea"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md transition-colors duration-200 shadow-sm"
            title="Edit Target Area"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            <span class="hidden md:inline">{{ t('modals.view_only_target_area.edit_target_area') }}</span>
          </button>
        </div>
      </div>

      <!-- V2 Map Section -->
      <div class="flex flex-col md:flex-row gap-0" :style="isMobile ? 'height: calc(100vh - 220px);' : 'height: calc(100vh - 300px);'">
        <div class="w-full h-full">
          <!-- Show mode: existing target area, not in edit mode -->
          <TargetAreaShow
            v-if="((isEditing && !isEditMode) || props.viewOnly) && apiToken"
            :target-area-external-id="currentTargetAreaId"
            :api-base-url="sparrowBaseUrl"
            :api-key="apiToken"
            :village-radius-km="50"
            :training-center-lat="tcLat"
            :training-center-lng="tcLng"
            :show-village-list="props.showVillageList"
            :on-token-refresh="handleTokenRefresh"
            :on-auth-failure="handleAuthFailure"
          />

          <!-- Edit mode: editing existing target area -->
          <TargetAreaCreateEdit
            v-else-if="isEditing && isEditMode && apiToken"
            :training-center-lat="tcLat"
            :training-center-lng="tcLng"
            :country-iso3-code="props.target_area_data[0].country_iso3_code"
            :api-base-url="sparrowBaseUrl"
            :api-key="apiToken"
            :target-area-external-id="currentTargetAreaId"
            :target-area-external-system-base-url="imetricsBaseUrl"
            :training-center-id="props.target_area_data[0].training_center_id"
            :default-radius-km="5"
            :max-radius-km="30"
            :min-radius-km="1"
            :on-token-refresh="handleTokenRefresh"
            :on-auth-failure="handleAuthFailure"
            @created="onTargetAreaEditSaved"
            @deleted="onTargetAreaDeleted"
            @error="onTargetAreaError"
          />

          <!-- Create mode: no target area yet -->
          <TargetAreaCreateEdit
            v-else-if="!isEditing && targetAreasEnabled && apiToken"
            :training-center-lat="tcLat"
            :training-center-lng="tcLng"
            :country-iso3-code="props.target_area_data[0].country_iso3_code"
            :api-base-url="sparrowBaseUrl"
            :api-key="apiToken"
            :target-area-external-system-base-url="imetricsBaseUrl"
            :training-center-id="props.target_area_data[0].training_center_id"
            :default-radius-km="5"
            :max-radius-km="30"
            :min-radius-km="1"
            :on-token-refresh="handleTokenRefresh"
            :on-auth-failure="handleAuthFailure"
            @created="onTargetAreaCreated"
            @error="onTargetAreaError"
          />

          <!-- Target areas not enabled -->
          <div v-else class="flex items-center justify-center h-full text-gray-500">
            {{ t('modals.kestrel_map.target_areas_not_enabled') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Batches List (only show on desktop when editing and not in manual survey mode) -->
    <div v-if="isEditing && !isMobile && !isManualSurveyMode" class="mt-2">
      <survey-batches
        :target-area-external-id="currentTargetAreaId || props.target_area_data[0].target_area_external_id"
        :training-center-name="props.target_area_data[0].name"
        :training-center-id="props.target_area_data[0].id"
        :preselected-villages="preselectedVillageCodes"
        :view-only="props.viewOnly"
        :mobile-view="false"
      />
    </div>

    <!-- Loading Overlay -->
    <div
      class="z-[9999] flex h-full w-full absolute left-0 top-0 justify-center items-center transition-opacity"
      v-if="isLoading"
    >
      <div class="absolute left-0 top-0 h-full w-full bg-gray-600 opacity-40"></div>
      <div class="text-[2.5rem]">{{ t('modals.kestrel_map.loading') }}</div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, onUnmounted } from 'vue'
  import { TargetAreaShow, TargetAreaCreateEdit, ManualSurvey, PendingSurveysBanner } from '@ishare/kestrel-vue'
  import '@ishare/kestrel-vue/style.css'
  import 'maplibre-gl/dist/maplibre-gl.css'
  import i18n from '@utils/i18n'
  import FlashMessageService from '@services/FlashMessages'
  import SurveyBatches from '@components/SurveyBatches'
  import { useSparrowAuth } from '@composables/useSparrowAuth'

  const { t } = i18n.global

  const props = defineProps({
    target_area_data: Array,
    viewOnly: {
      type: Boolean,
      default: false
    },
    showVillageList: {
      type: Boolean,
      default: true
    }
  })

  // kestrel-vue v2 appends /api/v2/... internally, so strip the trailing /api
  const sparrowBaseUrl = computed(() => (props.target_area_data[0]?.kestrel_api_url || '').replace(/\/api\/?$/, ''))
  const countryIso3 = computed(() => props.target_area_data[0]?.country_iso3_code)

  // iMetrics base URL for routing target area writes through iMetrics instead of Sparrow
  const imetricsBaseUrl = computed(() => `${window.location.protocol}//${window.location.host}`)

  const { token: apiToken, fetchToken: fetchConstrainedToken, handleTokenRefresh, handleAuthFailure } = useSparrowAuth({
    countryIso3,
    capabilities: ['target_area.view', 'target_area.edit', 'target_area.deactivate', 'boundary.view:all', 'boundary.view_shading:all', 'batch.create_adhoc'],
    onError: () => FlashMessageService.error(t('modals.kestrel_map.error_messages.initializing_map')),
  })

  // Training center coordinates
  const tcLat = ref(undefined)
  const tcLng = ref(undefined)
  const center = ref({ lat: 0, lng: 0 })

  // Component state
  let isEditing = ref(false)
  let isEditMode = ref(false)
  let currentTargetAreaId = ref(null)
  let isLoading = ref(false)
  let preselectedVillageCodes = ref([])

  // Manual survey mode state
  let isManualSurveyMode = ref(false)

  // PDF generation state
  let isPdfGenerating = ref(false)
  let pdfPollingInterval = ref(null)
  let pdfPollingTimeout = ref(null)
  let isPollingInProgress = ref(false)
  let generateSecurePdf = ref(false)

  // Mobile state
  const windowWidth = ref(window.innerWidth)
  const isMobile = computed(() => windowWidth.value < 768)

  // Check if target areas are enabled for this training center
  const targetAreasEnabled = computed(() => {
    return props.target_area_data?.[0]?.target_areas_enabled === true
  })

  onMounted(async () => {
    window.addEventListener('resize', handleResize)

    try {
      // Set coordinates and editing state BEFORE fetching the token.
      // apiToken becoming truthy triggers reactive v-if rendering, so all
      // dependent state (isEditing, tcLat/tcLng) must be ready first.
      const data = props.target_area_data.filter(x => x.lat != null)

      if (data.length !== 0) {
        let overallLat = 0
        let overallLong = 0

        data.forEach(i => {
          overallLat += parseFloat(i.lat)
          overallLong += parseFloat(i.long)
        })

        overallLat /= data.length
        overallLong /= data.length

        center.value = { lat: overallLat, lng: overallLong }
        tcLat.value = parseFloat(data[0].lat)
        tcLng.value = parseFloat(data[0].long)
      }

      if (props.target_area_data[0].target_area_external_id) {
        isEditing.value = true
        currentTargetAreaId.value = props.target_area_data[0].target_area_external_id
      }
    } catch (err) {
      console.error('Error setting up target area state:', err)
    }

    try {
      await fetchConstrainedToken()
    } catch (err) {
      console.error('Failed to fetch constrained token:', err)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.initializing_map'))
    }

  })

  onUnmounted(() => {
    if (pdfPollingInterval.value) {
      clearInterval(pdfPollingInterval.value)
    }
    if (pdfPollingTimeout.value) {
      clearTimeout(pdfPollingTimeout.value)
    }
    window.removeEventListener('resize', handleResize)
  })

  function handleResize() {
    windowWidth.value = window.innerWidth
  }

  // ---- Target Area Create/Edit handlers ----
  // Writes are routed through iMetrics via the routeResolver. The iMetrics
  // controller saves locally and syncs to Sparrow, so no separate sync call
  // is needed here.

  function onTargetAreaCreated(targetArea) {
    console.log('Target area created:', targetArea)
    isEditing.value = true
    currentTargetAreaId.value = targetArea.externalId
    FlashMessageService.success(t('modals.kestrel_map.success_messages.target_area_created'))
  }

  function onTargetAreaEditSaved(targetArea) {
    isEditMode.value = false
    currentTargetAreaId.value = targetArea.externalId
    FlashMessageService.success(t('modals.kestrel_map.success_messages.target_area_updated'))
  }

  function onTargetAreaDeleted(externalId) {
    // Deactivate was routed through iMetrics via routeResolver — iMetrics
    // deactivated locally and synced to Sparrow. Just show success and redirect.
    FlashMessageService.success(t('modals.kestrel_map.success_messages.target_area_deleted'))

    const trainingCenterId = props.target_area_data[0]?.training_center_id
    if (trainingCenterId) {
      window.location.href = `/training_centers/${trainingCenterId}`
    }
  }

  function onTargetAreaError(message) {
    console.error('Target area error:', message)
    FlashMessageService.error(message || t('modals.kestrel_map.error_messages.creating_target_area'))
  }

  function editTargetArea() {
    const trainingCenterId = props.target_area_data[0]?.training_center_id
    if (trainingCenterId) {
      window.location.href = `/training_centers/${trainingCenterId}/target_area`
    }
  }

  // ---- PDF Generation ----

  async function generatePDF() {
    if (!currentTargetAreaId.value) {
      FlashMessageService.error(t('modals.kestrel_map.error_messages.no_target_area'))
      return
    }

    if (pdfPollingInterval.value) {
      clearTimeout(pdfPollingInterval.value)
      pdfPollingInterval.value = null
    }
    if (pdfPollingTimeout.value) {
      clearTimeout(pdfPollingTimeout.value)
      pdfPollingTimeout.value = null
    }
    isPollingInProgress.value = false

    try {
      isPdfGenerating.value = true

      const response = await fetch(`/target_areas/${currentTargetAreaId.value}/generate_pdf.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          restricted: generateSecurePdf.value
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate PDF')
      }

      const pdfJobId = data.pdf_job_id

      if (!pdfJobId) {
        isPdfGenerating.value = false
        FlashMessageService.error(t('modals.kestrel_map.error_messages.no_polling_url'))
        return
      }

      FlashMessageService.success(t('modals.kestrel_map.success_messages.pdf_generating'))

      pdfPollingTimeout.value = setTimeout(() => {
        if (pdfPollingInterval.value) {
          clearTimeout(pdfPollingInterval.value)
          pdfPollingInterval.value = null
        }
        isPollingInProgress.value = false
        isPdfGenerating.value = false
        FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_timeout'))
      }, 30000)

      const pollStatus = async () => {
        if (isPollingInProgress.value) return

        isPollingInProgress.value = true

        try {
          const pollResponse = await fetch(`/target_areas/${currentTargetAreaId.value}/check_pdf_status.json?pdf_job_id=${pdfJobId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            }
          })

          if (!pollResponse.ok) {
            throw new Error(`Failed to check PDF status: ${pollResponse.status}`)
          }

          const pollData = await pollResponse.json()

          if (pollData.cached_resource_request?.cached_resource?.download_url) {
            clearTimeout(pdfPollingTimeout.value)
            pdfPollingTimeout.value = null
            if (pdfPollingInterval.value) {
              clearTimeout(pdfPollingInterval.value)
              pdfPollingInterval.value = null
            }
            isPollingInProgress.value = false
            isPdfGenerating.value = false

            FlashMessageService.success(t('modals.kestrel_map.success_messages.pdf_ready'))
            window.open(pollData.cached_resource_request.cached_resource.download_url, '_blank')
            return
          } else if (pollData.state === 'failed') {
            clearTimeout(pdfPollingTimeout.value)
            pdfPollingTimeout.value = null
            if (pdfPollingInterval.value) {
              clearTimeout(pdfPollingInterval.value)
              pdfPollingInterval.value = null
            }
            isPollingInProgress.value = false
            isPdfGenerating.value = false

            FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_generation_failed', {
              error: pollData.error_message || 'Unknown error'
            }))
            return
          }

          isPollingInProgress.value = false
          pdfPollingInterval.value = setTimeout(pollStatus, 1000)
        } catch (pollError) {
          console.error('Error polling PDF status:', pollError)
          clearTimeout(pdfPollingTimeout.value)
          pdfPollingTimeout.value = null
          if (pdfPollingInterval.value) {
            clearTimeout(pdfPollingInterval.value)
            pdfPollingInterval.value = null
          }
          isPollingInProgress.value = false
          isPdfGenerating.value = false
          FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_polling_failed'))
        }
      }

      pollStatus()
    } catch (error) {
      console.error('Error generating PDF:', error)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_generation_failed', { error: error.message }))
      isPdfGenerating.value = false
      if (pdfPollingInterval.value) {
        clearTimeout(pdfPollingInterval.value)
        pdfPollingInterval.value = null
      }
      if (pdfPollingTimeout.value) {
        clearTimeout(pdfPollingTimeout.value)
        pdfPollingTimeout.value = null
      }
      isPollingInProgress.value = false
    }
  }
</script>
